import { useCallback } from 'react';
import { toast } from 'sonner';
import {
  fetchInvoices,
  type Invoice,
  type ReleaseStatus,
} from '@/integrations/faturamento/client';
import { measurementStatusLabel } from '@/components/financeiro/contestacao/measurementStatus';
import { invoiceStatusLabel } from '@/components/financeiro/invoiceStatus';
import { excludeCutoffItems } from '@/config/legacyResicleanCutoff';
import { invoiceScopeFilter } from '@/hooks/invoiceScopeFilter';

const EXPORT_PAGE_SIZE = 1000;
const CONCURRENT_REQUESTS = 3;

interface UseInvoicesCSVExportParams {
  dueDateRange?: { start: Date | null; end: Date | null } | null;
  /** Competências específicas (`YYYY-MM`), não um intervalo — ver GR-670. */
  billingPeriods?: string[];
  /** Geradores escolhidos (ids internos). A API expande a subárvore; o front não lista filiais. */
  generatorIds?: number[];
  /** Unidades escolhidas à mão (ids exatos do gerador pagador). */
  establishmentIds?: number[];
  cnpj?: string;
  status?: string;
  /** Filtro de estado da medição, o mesmo da barra de filtros. */
  releaseStatuses?: string[];
  /** `'FATURA'` e/ou `'MEDICAO'`. Os dois juntos (ou vazio) têm o mesmo efeito de ausente. */
  types?: string[];
}

/** Medição e fatura são o mesmo ciclo em fases diferentes — a OS na Omie é o que separa. */
function isInvoiced(invoice: Invoice): boolean {
  return !!invoice.os_code;
}

/**
 * O estado do registro, na mesma regra da coluna Status da tabela: fatura mostra o status da
 * cobrança na Omie; medição em análise mostra o estado do ciclo.
 */
function statusLabel(invoice: Invoice): string {
  if (invoice.status) return invoiceStatusLabel(invoice.status);
  if (invoice.release_status) return measurementStatusLabel(invoice.release_status);
  return '';
}

function toYYYYMMDD(d: Date): string {
  return d.toISOString().split('T')[0];
}

function formatCompetencia(competencia: string | null | undefined): string {
  if (!competencia) return '';
  const [year, month] = competencia.split('-').map(Number);
  if (!year || !month) return '';
  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  return `${months[month - 1]}/${year}`;
}

function formatDate(date: string | null | undefined): string {
  if (!date) return '';
  const [year, month, day] = date.split('-');
  if (!year || !month || !day) return date;
  return `${day}/${month}/${year}`;
}

function escapeCsvValue(value: unknown): string {
  if (value == null) return '';
  const s = String(value);
  if (s.includes(';') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

const yieldToMainThread = () =>
  new Promise<void>((resolve) => {
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => resolve());
    } else {
      setTimeout(resolve, 0);
    }
  });

export const useInvoicesCSVExport = ({
  dueDateRange,
  billingPeriods,
  generatorIds,
  establishmentIds,
  cnpj,
  status,
  releaseStatuses,
  types,
}: UseInvoicesCSVExportParams) => {
  const handleExportCSV = useCallback(async () => {
    const toastId = toast.loading('Exportando extrato... 0%');

    try {
      const baseFilter = {
        due_date_from: dueDateRange?.start ? toYYYYMMDD(dueDateRange.start) : undefined,
        due_date_to: dueDateRange?.end ? toYYYYMMDD(dueDateRange.end) : undefined,
        billing_periods: billingPeriods?.length ? billingPeriods : undefined,
        ...invoiceScopeFilter(generatorIds, establishmentIds),
        cnpj: cnpj?.trim() || undefined,
        status: status && status !== 'all' ? status : undefined,
        // Sem isto a planilha trazia **só faturas**: a tabela pede as medições em análise
        // (`includeInAnalysis`) e a exportação não, então o CSV era um recorte diferente da tela
        // que o gerou — e justamente sem as linhas contestáveis.
        include_in_analysis: true,
        release_status: releaseStatuses?.length
          ? (releaseStatuses as ReleaseStatus[])
          : undefined,
        types: types?.length ? types : undefined,
      };

      const firstPage = await fetchInvoices({ ...baseFilter, page: 1, page_size: EXPORT_PAGE_SIZE });
      const allData: Invoice[] = [...(firstPage.data ?? [])];
      const totalPages = Math.max(firstPage.pagination?.last_page ?? 1, 1);
      let processedPages = 1;

      toast.loading(
        `Exportando extrato... ${Math.round((processedPages / totalPages) * 100)}% (${processedPages}/${totalPages} páginas)`,
        { id: toastId },
      );

      const remainingPages: number[] = [];
      for (let p = 2; p <= totalPages; p++) remainingPages.push(p);

      for (let i = 0; i < remainingPages.length; i += CONCURRENT_REQUESTS) {
        const batch = remainingPages.slice(i, i + CONCURRENT_REQUESTS);
        const results = await Promise.all(
          batch.map(async (page) => {
            const res = await fetchInvoices({ ...baseFilter, page, page_size: EXPORT_PAGE_SIZE });
            return { page, data: res.data ?? [] };
          }),
        );

        results.sort((a, b) => a.page - b.page).forEach((r) => allData.push(...r.data));
        processedPages += batch.length;

        toast.loading(
          `Exportando extrato... ${Math.round((processedPages / totalPages) * 100)}% (${processedPages}/${totalPages} páginas)`,
          { id: toastId },
        );

        await yieldToMainThread();
      }

      // MC-107: mesmo recorte da tela — esconde a fatura inteira dos geradores da carteira
      // Resiclean com competência no período retroativo fictício (jan-jul/2026).
      const visibleData = excludeCutoffItems(
        allData,
        (f) => f.paying_generator?.id,
        (f) => f.billing_period,
        'month',
      );

      if (visibleData.length === 0) {
        toast.warning('Nenhum registro encontrado para exportar', { id: toastId });
        return;
      }

      // As mesmas colunas da tabela, na mesma ordem: quem exporta espera a planilha do que está
      // vendo. Faltavam Tipo e Status — e o Nº caía vazio na medição, que não tem pedido na Omie.
      const headers = [
        'Nº', 'Tipo', 'Competência', 'Estabelecimento', 'CNPJ', 'Valor total', 'Vencimento', 'Status',
      ];
      const rows = visibleData.map((f) => [
        escapeCsvValue(f.numero_pedido ?? f.sfm_id),
        escapeCsvValue(isInvoiced(f) ? 'Fatura' : 'Medição'),
        escapeCsvValue(formatCompetencia(f.billing_period)),
        escapeCsvValue(f.paying_generator?.name),
        escapeCsvValue(f.paying_generator?.document),
        escapeCsvValue(
          new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(f.value),
        ),
        escapeCsvValue(formatDate(f.due_date)),
        escapeCsvValue(statusLabel(f)),
      ]);

      const csv = '\uFEFF' + [headers.join(';'), ...rows.map((r) => r.join(';'))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `extrato-financeiro_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      toast.success(`Relatório exportado com sucesso! (${visibleData.length} registros)`, { id: toastId });
    } catch (error) {
      console.error('Erro ao exportar CSV:', error);
      toast.error('Erro ao exportar relatório', { id: toastId });
    }
  }, [
    dueDateRange,
    billingPeriods,
    generatorIds,
    establishmentIds,
    cnpj,
    status,
    releaseStatuses,
    types,
  ]);

  return { handleExportCSV };
};
